/**
 * Image import validation. The file's own bytes decide the format (never the extension
 * or the browser-supplied MIME type), and the pixel size is read from the header before
 * anything is decoded, so an oversized image is rejected without allocating its pixels.
 */
export type ImageFormat = 'png' | 'jpeg' | 'webp';

export const IMAGE_MIME: Record<ImageFormat, string> = { png: 'image/png', jpeg: 'image/jpeg', webp: 'image/webp' };
export const IMAGE_ACCEPT = '.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp';

export const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
export const MAX_IMAGE_SIDE = 4096;
/** 4096 × 4096 = 16.7 MP → 64 MB of RGBA once decoded. */
export const MAX_IMAGE_PIXELS = 4096 * 4096;

export class ImageFileError extends Error {
  readonly code: 'empty' | 'too-large' | 'unsupported' | 'corrupt' | 'dimensions';
  constructor(code: ImageFileError['code'], message: string) {
    super(message);
    this.name = 'ImageFileError';
    this.code = code;
  }
}

const ascii = (b: Uint8Array, at: number, text: string) => [...text].every((ch, i) => b[at + i] === ch.charCodeAt(0));

export function sniffImage(b: Uint8Array): ImageFormat | null {
  if (b[0] === 0x89 && ascii(b, 1, 'PNG') && b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a) return 'png';
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'jpeg';
  if (ascii(b, 0, 'RIFF') && ascii(b, 8, 'WEBP')) return 'webp';
  return null;
}

const u16be = (b: Uint8Array, i: number) => (b[i]! << 8) | b[i + 1]!;
const u32be = (b: Uint8Array, i: number) => ((b[i]! << 24) >>> 0) + (b[i + 1]! << 16) + (b[i + 2]! << 8) + b[i + 3]!;
const u24le = (b: Uint8Array, i: number) => b[i]! | (b[i + 1]! << 8) | (b[i + 2]! << 16);

/** Width/height from the header, or null when the header is malformed or truncated. */
export function readImageSize(format: ImageFormat, b: Uint8Array): { width: number; height: number } | null {
  switch (format) {
    case 'png':
      // IHDR must be the first chunk.
      if (b.length < 24 || !ascii(b, 12, 'IHDR')) return null;
      return { width: u32be(b, 16), height: u32be(b, 20) };
    case 'jpeg': {
      let i = 2;
      while (i + 9 < b.length) {
        if (b[i] !== 0xff) return null;
        const marker = b[i + 1]!;
        if (marker === 0xff) {
          i++;
          continue;
        }
        // Standalone markers without a length.
        if (marker === 0xd8 || (marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) {
          i += 2;
          continue;
        }
        const length = u16be(b, i + 2);
        if (length < 2) return null;
        // SOF0–SOF15 except DHT (C4), JPG (C8) and DAC (CC).
        if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
          return { height: u16be(b, i + 5), width: u16be(b, i + 7) };
        }
        if (marker === 0xda || marker === 0xd9) return null; // image data before any frame header
        i += 2 + length;
      }
      return null;
    }
    case 'webp': {
      if (b.length < 30) return null;
      if (ascii(b, 12, 'VP8 ')) return { width: (b[26]! | (b[27]! << 8)) & 0x3fff, height: (b[28]! | (b[29]! << 8)) & 0x3fff };
      if (ascii(b, 12, 'VP8L')) {
        if (b[20] !== 0x2f) return null;
        const bits = b[21]! | (b[22]! << 8) | (b[23]! << 16) | (b[24]! << 24);
        return { width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1 };
      }
      if (ascii(b, 12, 'VP8X')) return { width: u24le(b, 24) + 1, height: u24le(b, 27) + 1 };
      return null;
    }
  }
}

/** How many bytes of the file `validateImageFile` needs (JPEG frame headers can be late). */
export const IMAGE_HEADER_BYTES = 256 * 1024;

export function validateImageFile(name: string, sizeBytes: number, header: Uint8Array): { format: ImageFormat; mimeType: string; width: number; height: number } {
  if (sizeBytes === 0) throw new ImageFileError('empty', `“${name}” is empty.`);
  if (sizeBytes > MAX_IMAGE_BYTES) {
    throw new ImageFileError('too-large', `“${name}” is ${(sizeBytes / 1048576).toFixed(1)} MB; images are limited to ${MAX_IMAGE_BYTES / 1048576} MB.`);
  }
  const format = sniffImage(header);
  if (!format) throw new ImageFileError('unsupported', `“${name}” is not a PNG, JPEG or WebP image.`);
  const size = readImageSize(format, header);
  if (!size || size.width < 1 || size.height < 1) throw new ImageFileError('corrupt', `“${name}” looks like a ${format.toUpperCase()} file but its header is damaged.`);
  if (size.width > MAX_IMAGE_SIDE || size.height > MAX_IMAGE_SIDE || size.width * size.height > MAX_IMAGE_PIXELS) {
    throw new ImageFileError('dimensions', `“${name}” is ${size.width} × ${size.height} px; images are limited to ${MAX_IMAGE_SIDE} px per side (${(MAX_IMAGE_PIXELS / 1e6).toFixed(1)} MP).`);
  }
  return { format, mimeType: IMAGE_MIME[format], ...size };
}
